import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ConfirmationResult } from 'firebase/auth';
import Header from '../components/Header';
import firebaseAuthService from '../services/firebase/FirebaseAuthService';
import { AuditService } from '../services/firebase/AuditService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';

const ClientLogin: React.FC = () => {
  const [loginMethod, setLoginMethod] = useState<'phone' | 'email'>('email');
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Phone OTP state — stores the Firebase confirmation result needed for verification
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);

  // Resend cooldown
  const [resendCooldown, setResendCooldown] = useState(0);

  const navigate = useNavigate();
  const { userProfile, isClient, refreshProfile } = useAuth();
  const toast = useToast();

  // Redirect to dashboard if already logged in as client
  useEffect(() => {
    if (userProfile && isClient) {
      navigate('/client-dashboard', { replace: true });
    }
  }, [userProfile, isClient, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '');

    if (digits.length >= 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length >= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length >= 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return digits;
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setIsLoading(true);

    try {
      const formattedPhone = '+1' + digits;
      const result = await firebaseAuthService.sendPhoneOTP(formattedPhone);
      setConfirmationResult(result);
      setStep('otp');
      setResendCooldown(60);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (otp.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    if (!confirmationResult) {
      setError('Verification session expired. Please request a new code.');
      setStep('phone');
      return;
    }

    setIsLoading(true);

    try {
      const userCredential = await firebaseAuthService.verifyPhoneOTP(confirmationResult, otp);
      if (userCredential?.user) {
        AuditService.logAuthEvent(userCredential.user.uid, 'login', 'client');
      }
      navigate('/client-dashboard');
    } catch (err: any) {
      setError(err.message || 'Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;

    setError('');
    setIsLoading(true);

    try {
      const formattedPhone = '+1' + phoneNumber.replace(/\D/g, '');
      const result = await firebaseAuthService.sendPhoneOTP(formattedPhone);
      setConfirmationResult(result);
      setResendCooldown(60);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to resend code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setIsLoading(true);

    try {
      const userCredential = await firebaseAuthService.loginWithEmail(email, password, 'client');
      // Refresh profile so AuthContext/ProtectedRoute sees updated client role
      await refreshProfile();
      // Navigate after profile is refreshed
      navigate('/client-dashboard', { replace: true });
      // Non-blocking: ensure profile (fire-and-forget)
      firebaseAuthService.ensureClientProfile(userCredential.user).catch(() => {});
      AuditService.logAuthEvent(userCredential.user.uid, 'login', 'client');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="app">
      <Header />

      <main id="main-content" className="page">
        <div className="container" style={{ paddingBottom: '80px' }}>
          <div style={{
            maxWidth: '400px',
            margin: '0 auto',
            paddingTop: '3rem'
          }}>
            {/* Header */}
            <div className="text-center" style={{ marginBottom: '2rem', fontSize: '2rem', }}>
                My ClinicCore
              <h1 className="page-title" style={{ marginBottom: '0.5rem' }}>
                Client Login
              </h1>
              <p style={{ color: '#d1d5db', fontSize: '1rem' }}>
                {loginMethod === 'email'
                  ? 'Enter your email and password to login'
                  : step === 'phone'
                    ? 'Enter your phone number to receive a verification code'
                    : 'Enter the verification code sent to your phone'
                }
              </p>
            </div>

            {/* Login Method Tabs */}
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              marginBottom: '1rem'
            }}>
              <button
                onClick={() => setLoginMethod('email')}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: loginMethod === 'email' ? '#D4A017' : '#374151',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: loginMethod === 'email' ? '600' : '400'
                }}
              >
                Email Login
              </button>
              <button
                onClick={() => setLoginMethod('phone')}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: loginMethod === 'phone' ? '#D4A017' : '#374151',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: loginMethod === 'phone' ? '600' : '400'
                }}
              >
                Phone/SMS Login
              </button>
            </div>

            {/* Login Form */}
            <div className="card">{loginMethod === 'email' ? (
                /* Email/Password Login */
                <form onSubmit={handleEmailLogin} aria-label="Client email login form">
                  <div className="form-group">
                    <label htmlFor="client-email" className="form-label">
                      Email Address
                    </label>
                    <input
                      id="client-email"
                      type="email"
                      className="form-input"
                      autoComplete="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      required
                      aria-required="true"
                      aria-invalid={error ? 'true' : undefined}
                      aria-describedby={error ? 'client-login-error' : undefined}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="client-password" className="form-label">
                      Password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        id="client-password"
                        type={showPassword ? 'text' : 'password'}
                        className="form-input"
                        autoComplete="current-password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={isLoading}
                        required
                        aria-required="true"
                        aria-invalid={error ? 'true' : undefined}
                        aria-describedby={error ? 'client-login-error' : undefined}
                        style={{ paddingRight: '3rem' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        style={{
                          position: 'absolute',
                          right: '0.75rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: '#d1d5db',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          minWidth: '44px',
                          minHeight: '44px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        disabled={isLoading}
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div id="client-login-error" className="form-error" role="alert" aria-live="assertive" style={{ marginBottom: '1rem' }}>
                      {error}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!email.trim()) {
                          setError('Please enter your email address first.');
                          return;
                        }
                        try {
                          await firebaseAuthService.resetPassword(email);
                          setError('');
                          toast.success('Password Reset Sent', `A reset link has been sent to ${email}. Please check your inbox.`);
                        } catch (err: any) {
                          setError(err.message || 'Failed to send reset email.');
                        }
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#D4A017',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        textDecoration: 'underline',
                        padding: 0,
                      }}
                      disabled={isLoading}
                    >
                      Forgot password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Signing In...' : 'Sign In'}
                  </button>

                </form>
              ) : step === 'phone' ? (
                /* Phone Number Step */
                <form onSubmit={handlePhoneSubmit} aria-label="Client phone login form">
                  <div className="form-group">
                    <label htmlFor="client-phone" className="form-label">
                      Phone Number
                    </label>
                    <input
                      id="client-phone"
                      type="tel"
                      className="form-input"
                      placeholder="(555) 123-4567"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                      maxLength={14}
                      required
                      aria-required="true"
                      aria-invalid={error ? 'true' : undefined}
                      aria-describedby={error ? 'client-login-error' : undefined}
                      disabled={isLoading}
                    />
                  </div>

                  {error && (
                    <div id="client-login-error" className="form-error" role="alert" aria-live="assertive" style={{ marginBottom: '1rem' }}>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Send Verification Code'}
                  </button>
                </form>
              ) : (
                /* OTP Verification Step */
                <form onSubmit={handleOtpSubmit} aria-label="OTP verification form">
                  <div className="form-group">
                    <label htmlFor="client-otp" className="form-label">
                      Verification Code
                    </label>
                    <input
                      id="client-otp"
                      type="text"
                      className="form-input"
                      placeholder="123456"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      maxLength={6}
                      required
                      aria-required="true"
                      aria-invalid={error ? 'true' : undefined}
                      aria-describedby={error ? 'client-login-error' : undefined}
                      disabled={isLoading}
                      style={{
                        textAlign: 'center',
                        letterSpacing: '0.5rem',
                        fontSize: '1.5rem',
                        fontFamily: 'monospace'
                      }}
                    />
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#d1d5db',
                      marginTop: '0.5rem'
                    }}>
                      Code sent to {phoneNumber}
                    </p>
                  </div>

                  {error && (
                    <div id="client-login-error" className="form-error" role="alert" aria-live="assertive" style={{ marginBottom: '1rem' }}>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ width: '100%', marginBottom: '1rem' }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Verifying...' : 'Verify & Login'}
                  </button>

                  {/* Resend and Back Options */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.875rem'
                  }}>
                    <button
                      type="button"
                      onClick={() => {
                        setStep('phone');
                        setConfirmationResult(null);
                        setOtp('');
                        setError('');
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#D4A017',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                      disabled={isLoading}
                    >
                      Change Number
                    </button>
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: resendCooldown > 0 ? '#6b7280' : '#D4A017',
                        cursor: resendCooldown > 0 ? 'default' : 'pointer',
                        textDecoration: resendCooldown > 0 ? 'none' : 'underline'
                      }}
                      disabled={isLoading || resendCooldown > 0}
                      aria-label={resendCooldown > 0 ? `Resend available in ${resendCooldown} seconds` : 'Resend verification code'}
                    >
                      {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend Code'}
                    </button>
                  </div>
                </form>
              )}

              {/* reCAPTCHA container for Firebase phone auth */}
              <div id="recaptcha-container" style={{ display: 'none' }}></div>
            </div>

            {/* Footer Links */}
            <div className="text-center" style={{ marginTop: '2rem' }}>
              {/* Patients don't self-register — the practice creates the
                  account and emails an invitation link. */}
              <p style={{ color: '#d1d5db', fontSize: '0.875rem' }}>
                New to My ClinicCore? Your provider's office will send you an
                invitation to set up your account.
              </p>
              <p style={{ color: '#d1d5db', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                Are you a healthcare provider?{' '}
                <Link
                  to="/provider-login"
                  style={{ color: '#D4A017', textDecoration: 'none' }}
                >
                  Provider Login
                </Link>
              </p>
              <Link
                to="/"
                style={{
                  color: '#d1d5db',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  display: 'inline-block',
                  marginTop: '0.5rem'
                }}
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          textAlign: 'center',
          padding: '1rem',
          backgroundColor: '#16325B',
          borderTop: '1px solid #374151',
          zIndex: 1000
        }}>
          <p style={{
            color: '#d1d5db',
            fontSize: '0.875rem',
            margin: 0
          }}>
            &copy; 2026 My ClinicCore |{' '}
              <button type="button" style={{ color: '#6CA6C1', cursor: 'pointer', background: 'none', border: 'none', padding: '0.25rem', fontSize: 'inherit', textDecoration: 'underline' }}>BAA</button> |{' '}
              <button type="button" style={{ color: '#6CA6C1', cursor: 'pointer', background: 'none', border: 'none', padding: '0.25rem', fontSize: 'inherit', textDecoration: 'underline' }}>Terms</button> |{' '}
              <button type="button" style={{ color: '#6CA6C1', cursor: 'pointer', background: 'none', border: 'none', padding: '0.25rem', fontSize: 'inherit', textDecoration: 'underline' }}>Privacy</button>
          </p>

          {/* CPT Statement — collapsible */}
          <div style={{
            marginTop: '1rem',
            padding: '0.5rem',
            backgroundColor: '#1f2937',
            borderRadius: '0.25rem',
            border: '1px solid #374151'
          }}>
            <button
              type="button"
              onClick={() => {
                const el = document.getElementById('cpt-details');
                if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
              }}
              aria-expanded="false"
              aria-controls="cpt-details"
              style={{
                color: '#d1d5db',
                fontSize: '0.75rem',
                margin: 0,
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                background: 'none',
                border: 'none',
                padding: 0
              }}
            >
              <span>My ClinicCore partners with the AMA to use CPT codes</span>
              <span style={{ fontSize: '0.65rem', color: '#6CA6C1', marginLeft: '8px' }}>tap to expand</span>
            </button>
            <p id="cpt-details" style={{
              color: '#ffffff',
              fontSize: '0.875rem',
              margin: '0.25rem 0 0 0',
              lineHeight: '1.3',
              textAlign: 'left',
              maxWidth: '800px',
              marginLeft: 'auto',
              marginRight: 'auto',
              display: 'none'
            }}>
              CPT &copy; 2026 American Medical Association. All Rights reserved. CPT is a registered trademark of the American Medical Association. Fee schedules, relative value units, conversion factors and/or related components are not assigned by the AMA, are not part of CPT, and the AMA is not recommending their use. The AMA does not directly or indirectly practice medicine or dispense medical services. The AMA assumes no liability for data contained or not contained herein.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClientLogin;
